import React from 'react';
import { connect } from 'react-redux';
import { InView } from 'react-intersection-observer';
import { Box, BoxProps, Card, CardProps, Icon, Link, Typography } from '@drone-js/core';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/common/ActivityCard/ActivityCard.selectors';
import ActivityCardImage from 'component/common/ActivityCard/Image/ActivityCardImage';
import DebugInformation from 'component/common/ActivityCard/DebugInformation/DebugInformation';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { Price } from 'component/common/Price/Price';
import ActivityCardHighlightBanner from 'component/common/ActivityCard/HighlightBanner/ActivityCardHighlightBanner';
import ActivityCardStarRating from 'component/common/ActivityCard/ActivityCardStarRating';
import ActivityCardBadge from 'component/common/Badge/ActivityCardBadge';
import { PriceDisplayBehavior } from 'core/type/behaviors.types';
import { ActivityBadgeCategory, ActivityBadgeCategoryProps } from 'component/common/Badge/BadgeService.types';
import RibbonContainer from 'component/common/RibbonContainer/RibbonContainer';
import { MAX_RECENT_SEARCH, setRecentlySearch } from 'component/common/TextSearch/suggestionUtils';
import { RecentSearchItem } from 'core/type/SuggestionResult.types';
import { TextSearchType } from 'core/type/TextSearch.types';
import { getDetailUrl } from 'core/utils/activityUrlGenerator';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';
import { ExperimentId } from 'core/type/ExperimentId';

const CardPropsMobile: CardProps = {
    px: '16',
    py: '12',
    variant: undefined,
    type: 'interactive',
    height: 'auto',
    display: 'flex',
    alignItems: 'flex-start',
    borderRadius: '0',
};

const CardPropsDesktop: CardProps = {
    px: '0',
    py: '0',
    variant: 'elevation-s',
    type: 'interactive',
    height: '100%',
    display: 'block',
    borderRadius: '16',
};

const BoxPropsCity: CardProps = {
    pe: '0',
    height: 'auto',
    width: '270px',
    me: '12',
    type: 'interactive',
    variant: 'elevation-s',
    my: '4',
};

const cardPropsBuilder = (isMobile: boolean, isPopularActivitiesCard = false): CardProps => {
    if (isPopularActivitiesCard) {
        return BoxPropsCity;
    }
    return isMobile ? CardPropsMobile : CardPropsDesktop;
};

const BoxPropsMobile: BoxProps = {
    ps: '12',
    pe: '0',
    height: 'auto',
    width: '100%',
};

const getBoxHeightDesktop = (shouldAdjustHeightForBadgePoints: boolean, showInclusiveTax: boolean, isBadgeV2Enabled: boolean, isAutoAppliedPricePeekEnabled: boolean): string => {
    if (!isAutoAppliedPricePeekEnabled) return shouldAdjustHeightForBadgePoints || showInclusiveTax || isBadgeV2Enabled ? '194px' : '184px';

    // The additionalItems contains items to show or hide, which must follow the same condition for all activities.
    // Otherwise, card size will vary for each activity
    const additionalItems = [showInclusiveTax];
    const lengthOfShowItems = additionalItems.filter((item) => item).length;
    // 220 is fit for two lines of title with rating, highlight badge, promocode badge (can be two lines), deal badge and price
    const cardHeight = 220 + 13 * lengthOfShowItems;
    return `${cardHeight}px`;
};

const BoxPropsDesktop = (shouldAdjustHeightForBadgePoints: boolean, showInclusiveTax: boolean, isBadgeV2Enabled: boolean, isAutoAppliedPricePeekEnabled: boolean): BoxProps => ({
    px: '16',
    pb: '16',
    pt: '12',
    height: getBoxHeightDesktop(shouldAdjustHeightForBadgePoints, showInclusiveTax, isBadgeV2Enabled, isAutoAppliedPricePeekEnabled),
    maxHeight: getBoxHeightDesktop(shouldAdjustHeightForBadgePoints, showInclusiveTax, isBadgeV2Enabled, isAutoAppliedPricePeekEnabled),
});

const boxPropsBuilder = (
    isMobile: boolean,
    shouldAdjustHeightForBadgePoints: boolean,
    showInclusiveTax: boolean,
    isBadgeV2Enabled: boolean,
    isAutoAppliedPricePeekEnabled: boolean,
): BoxProps => (isMobile ? BoxPropsMobile : BoxPropsDesktop(shouldAdjustHeightForBadgePoints, showInclusiveTax, isBadgeV2Enabled, isAutoAppliedPricePeekEnabled));

const ActivityCardComponent: React.FC<Props> = React.memo((props) => {
    const {
        activity,
        isPopularActivitiesCard,
        index,
        isSearchBoxFeatureGroup,
        isBadgeV2Enabled,
        screenSize,
        config,
        isRecentSearchFix,
        isAutoAppliedPricePeekEnabled,
        shouldShowCityName,
    } = props;
    const { activityHighlights, deals, keyAttributes, promocodes } = activity.badges || {};
    const cardIndex = index + 1;
    let isMobile = screenSize.mobile;
    const { behavior, cms, language } = config;
    const isPriceRoundingUp = behavior.common.price.roundingPriceBehavior;
    const ssrDisplayBehavior = behavior.common.price.ssrDisplayBehavior;
    const shouldAdjustHeightForBadgePoints = ssrDisplayBehavior === PriceDisplayBehavior.PriceWithBadgePoints;
    const { showInclusiveTax } = behavior.common;
    const promocodeBadgeSSR = useExperimentIsB(ExperimentId.ACTB_AUTO_APPLIED_PRICE_PEEK);

    if (props.isPopularActivitiesCard) {
        isMobile = false;
    }

    const highlightBadgeMobileProps: ActivityBadgeCategoryProps = {
        badgeMargin: {
            s: { mb: '8' },
            m: { mb: '8' },
            l: { mb: '8' },
        },
    };
    const highlightBadgeDesktopProps: ActivityBadgeCategoryProps = {
        badgeMaxWidth: { m: '220px', l: '220px' },
    };

    const onCardClick = () => {
        const recentClick: RecentSearchItem = {
            id: activity.id,
            type: TextSearchType.Activity,
            activityName: activity.title,
            url: isRecentSearchFix ? getDetailUrl(activity.id, language.cultureCode) : activity.url,
            isUrlWithPrefix: true,
            cityName: activity.cityName || '',
            cityId: activity.cityId || 0,
            countryName: activity.countryName || '',
            supplierId: activity.supplierId,
        };
        isSearchBoxFeatureGroup && setRecentlySearch(recentClick, MAX_RECENT_SEARCH);
        props.onCardClick();
    };
    return (
        <React.Fragment key={activity.id}>
            <DebugInformation
                supplierId={activity.supplierId}
                supplierActivityCode={activity.supplierActivityCode}
                activityId={activity.id}
                masterActivityId={activity.masterActivityId}
                categories={activity.categories}
                rankScore={activity.rankScore}
            />
            <Link
                key={activity.id}
                onClick={onCardClick}
                onMouseEnter={props.onCardHover}
                href={activity.url}
                target={activity.openDetailPageInNewTab ? '_blank' : '_self'}
                data-element-name="activities-card-content"
                data-testid="activities-card-content"
                data-element-index={cardIndex}
                data-activity-id={activity.id}
            >
                {activity.isHighlight && isMobile && <ActivityCardHighlightBanner isMobileScreen={isMobile} />}
                <Card
                    data-testid={isPopularActivitiesCard ? 'activities-card-wrapper-popular' : 'activities-card-wrapper'}
                    status={activity.isHighlight ? 'selected' : 'default'}
                    overflow="hidden"
                    position="relative"
                    {...cardPropsBuilder(isMobile, props.isPopularActivitiesCard)}
                >
                    {activity.isHighlight && !isMobile && <ActivityCardHighlightBanner />}
                    <InView onChange={props.onCardSeen} delay={300} threshold={1} triggerOnce={true}>
                        <Box position="relative">
                            {isBadgeV2Enabled && !isMobile && activityHighlights && (
                                <RibbonContainer top="24px" start="16px" elementName="highlight-ribbon-badge">
                                    <ActivityCardBadge
                                        badges={activityHighlights}
                                        activityId={activity.id}
                                        badgeCategory={ActivityBadgeCategory.HIGHLIGHT}
                                        isBVariant={true}
                                        overrideCategoryProps={highlightBadgeDesktopProps}
                                        supplierId={activity.supplierId}
                                    />
                                </RibbonContainer>
                            )}
                            <ActivityCardImage index={activity.index} image={activity.image} onLoad={props.measureImageLoadFirstCard} isMobileScreen={isMobile} />
                        </Box>
                    </InView>
                    <Box
                        display="flex"
                        flexDirection="column"
                        {...boxPropsBuilder(isMobile, shouldAdjustHeightForBadgePoints, showInclusiveTax, isBadgeV2Enabled, isAutoAppliedPricePeekEnabled)}
                    >
                        {isBadgeV2Enabled && isMobile && activityHighlights && (
                            <ActivityCardBadge
                                badges={activityHighlights}
                                badgeCategory={ActivityBadgeCategory.HIGHLIGHT}
                                activityId={activity.id}
                                isBVariant={true}
                                overrideCategoryProps={highlightBadgeMobileProps}
                                supplierId={activity.supplierId}
                            />
                        )}
                        <Typography
                            data-element-name="activities-card-title"
                            key="activityTitle"
                            lang={activity.contentLocale}
                            variant={isMobile ? 'body-s-strong' : 'heading-default'}
                            my="0"
                            mb={isMobile ? '8' : '0'}
                            lineClamp={2}
                        >
                            {activity.title}
                        </Typography>
                        <ActivityCardStarRating activity={activity} isMobileScreen={isMobile} />
                        {!isBadgeV2Enabled && activityHighlights && (
                            <ActivityCardBadge
                                badges={activityHighlights}
                                badgeCategory={ActivityBadgeCategory.HIGHLIGHT}
                                activityId={activity.id}
                                supplierId={activity.supplierId}
                            />
                        )}
                        {shouldShowCityName && (
                            <Box display="flex" mt={isMobile ? '0' : '8'} mb={isMobile ? '8' : '0'} alignItems="center" data-testid="activities-card-city-name">
                                <Icon me="4" id="fill.symbol.pin" size="16" color="base.subtle" />
                                <Typography variant="caption-default" textColor="base.subtle">
                                    {activity.cityName}
                                </Typography>
                            </Box>
                        )}
                        {isBadgeV2Enabled && keyAttributes && (
                            <ActivityCardBadge
                                badges={keyAttributes}
                                badgeCategory={ActivityBadgeCategory.KEY_ATTRIBUTES}
                                activityId={activity.id}
                                isBVariant={isBadgeV2Enabled}
                                supplierId={activity.supplierId}
                            />
                        )}
                        {isBadgeV2Enabled && promocodeBadgeSSR && promocodes && (
                            <Box display="flex" pt="8" justifyContent="flex-start">
                                <ActivityCardBadge badges={promocodes} badgeCategory={ActivityBadgeCategory.PROMOCODE} activityId={activity.id} isBVariant={true} />
                            </Box>
                        )}
                        <Box
                            key="chargeBox"
                            display="flex"
                            flexDirection="column"
                            textAlign="end"
                            pb="0"
                            mt={isMobile ? '4' : 'auto'}
                            justifyContent="flex-end"
                            data-testid="activities-card-total-price"
                        >
                            {isBadgeV2Enabled && deals && (
                                <Box mb="4">
                                    <ActivityCardBadge
                                        badges={deals}
                                        badgeCategory={ActivityBadgeCategory.DEAL}
                                        activityId={activity.id}
                                        isBVariant={isBadgeV2Enabled}
                                        supplierId={activity.supplierId}
                                    />
                                </Box>
                            )}

                            {activity.pricingSummary && (
                                <Price
                                    pricingSummary={activity.pricingSummary}
                                    activityId={activity.id}
                                    isPriceRoundingUp={isPriceRoundingUp}
                                    priceJustifyContent="flex-end"
                                    displayBehavior={ssrDisplayBehavior}
                                    isMobileScreen={isMobile}
                                />
                            )}
                            {showInclusiveTax && (
                                <Typography variant="caption-default" textColor="base.subtle">
                                    {cms[319651]}
                                </Typography>
                            )}
                            {!isBadgeV2Enabled && deals && (
                                <ActivityCardBadge badges={deals} badgeCategory={ActivityBadgeCategory.DEAL} activityId={activity.id} supplierId={activity.supplierId} />
                            )}
                        </Box>
                    </Box>
                </Card>
            </Link>
        </React.Fragment>
    );
});
ActivityCardComponent.displayName = 'ActivityCardComponent';

export default connect(mapStateToProps, mapDispatchToProps)(withErrorBoundary(ActivityCardComponent));
